import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import {
  DATE_TIME_FORMAT,
  DATE_TIME_FORMAT_SHORTER,
  UTC_TIMEZONE
} from 'constants/constants';
import dayjs from 'dayjs';
import { FormikTouched } from 'formik';
import { FormikErrors } from 'formik';

import AcCard from '../../../../components/AcCard/AcCard';
import AcInput from '../../../../components/AcInput/AcInput';
import Typography from '../../../../components/Typography/Typography';
import { CouponFormValues } from '../../types';
import { applyExpirationModeChange, deriveExpirationMode } from '../../utils';

import {
  buildDateChangeHandler,
  buildTextFieldSlotProps,
  commonDateTimePickerSx,
  getDateFieldValue
} from './general-settings.utils';

import '../../../../style/forms.scss';

interface GeneralSettingsProps {
  couponFormValues: CouponFormValues;
  edit: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  errors: FormikErrors<CouponFormValues>;
  touched: FormikTouched<CouponFormValues>;
  setFieldTouched: (field: string, isTouched?: boolean | undefined) => void;
  setFieldValue: (field: string, value: any) => void;
  neverExpires: boolean;
  dup: boolean;
  updatedAt?: string;
  setNeverExpires: (value: boolean) => void;
}

export default function CouponsGeneralSettings({
  couponFormValues,
  edit,
  handleChange,
  handleBlur,
  errors,
  touched,
  setFieldTouched,
  setFieldValue,
  neverExpires,
  dup,
  updatedAt,
  setNeverExpires
}: GeneralSettingsProps) {
  return (
    <AcCard
      stackContainer={false}
      title="General Settings"
      sideHeader={
        updatedAt && (
          <Typography
            tag="span"
            style={{ fontSize: '12px', color: 'green', fontWeight: 'normal' }}
          >
            last updated {dayjs(updatedAt).format(DATE_TIME_FORMAT)}
          </Typography>
        )
      }
      customStyles={{
        border: '1px solid #cacbd4',
        borderRadius: '4px',
        padding: '1em'
      }}
    >
      <Grid container rowSpacing={2} columnSpacing={1.5}>
        <Grid item xs={4}>
          <AcInput
            header="Name"
            name="name"
            dataTestId="couponNameInput"
            value={couponFormValues.name}
            disabled={edit}
            onChange={handleChange}
            onBlur={handleBlur}
            required={true}
            error={Boolean(errors.name) && (touched.name || dup)}
            helperText={touched.name || dup ? errors.name?.toString() : ''}
          />
        </Grid>
        <Grid item xs={4}>
          <AcInput
            header="Discount %"
            name="discountPercentage"
            dataTestId="couponDiscountPercentageInput"
            value={couponFormValues.discountPercentage}
            disabled={edit}
            onChange={handleChange}
            onBlur={handleBlur}
            required={true}
            type="number"
            error={
              Boolean(errors.discountPercentage) && touched.discountPercentage
            }
            helperText={
              touched.discountPercentage
                ? errors.discountPercentage?.toString()
                : ''
            }
          />
        </Grid>
        <Grid item xs={4}></Grid>
        <Grid item xs={12}>
          <Stack direction="column" alignItems="flex-start" spacing={2}>
            <FormControl>
              <RadioGroup
                row
                value={
                  couponFormValues.expirationMode ||
                  deriveExpirationMode(
                    couponFormValues.startsAt,
                    couponFormValues.expiredBy
                  )
                }
                onChange={(e) => {
                  const value = e.target.value as any;
                  setFieldValue('expirationMode', value);
                  applyExpirationModeChange(
                    value,
                    setFieldValue,
                    setFieldTouched,
                    setNeverExpires
                  );
                }}
              >
                <FormControlLabel
                  value="no-expiration"
                  control={<Radio />}
                  label="No Expiration"
                />
                <FormControlLabel
                  value="start-only"
                  control={<Radio />}
                  label="Set start date"
                />
                <FormControlLabel
                  value="start-and-end"
                  control={<Radio />}
                  label="Set start and expiration date"
                />
              </RadioGroup>
            </FormControl>
            {couponFormValues.expirationMode !== 'no-expiration' && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <DateTimePicker
                    timezone={UTC_TIMEZONE}
                    disablePast
                    format={DATE_TIME_FORMAT_SHORTER}
                    value={getDateFieldValue(
                      couponFormValues as unknown as Record<string, unknown>,
                      'startsAt'
                    )}
                    onChange={buildDateChangeHandler(
                      'startsAt',
                      setFieldTouched,
                      setFieldValue
                    )}
                    sx={commonDateTimePickerSx}
                    slotProps={buildTextFieldSlotProps(
                      'Available from',
                      'startsAt',
                      errors as unknown as Record<string, unknown>,
                      touched as unknown as Record<string, boolean>,
                      edit
                    )}
                  />
                  {couponFormValues.expirationMode === 'start-and-end' && (
                    <DateTimePicker
                      timezone={UTC_TIMEZONE}
                      disablePast
                      format={DATE_TIME_FORMAT_SHORTER}
                      value={getDateFieldValue(
                        couponFormValues as unknown as Record<string, unknown>,
                        'expiredBy'
                      )}
                      onChange={buildDateChangeHandler(
                        'expiredBy',
                        setFieldTouched,
                        setFieldValue
                      )}
                      sx={commonDateTimePickerSx}
                      slotProps={buildTextFieldSlotProps(
                        'Expires on',
                        'expiredBy',
                        errors as unknown as Record<string, unknown>,
                        touched as unknown as Record<string, boolean>,
                        edit
                      )}
                    />
                  )}
                </Stack>
              </LocalizationProvider>
            )}
          </Stack>
        </Grid>
      </Grid>
    </AcCard>
  );
}
